"use client";

import { motion } from "framer-motion";
import { Server, Cloud, MessageSquare, Plug, Bot, LifeBuoy, Activity, GraduationCap } from "lucide-react";
import { Button } from "@/components/ui/button";

const services = [
  {
    icon: Server,
    title: "On-Premise AI Deployment",
    bullets: [
      "Private AI assistant installed on your infrastructure — Mac, Windows, Linux, or on-prem",
      "Enterprise-grade service configuration with logging, uptime, and auto-recovery",
      "All communication channels and core capabilities connected on day one",
    ],
    outcome: "Your own AI that runs inside your perimeter — zero data leaves your walls.",
  },
  {
    icon: Cloud,
    title: "Managed Cloud Hosting",
    bullets: [
      "Turnkey deployment on your preferred cloud or VPS provider",
      "Network security, access control, and remote management configured",
      "Multi-user ready for teams, departments, or subsidiaries",
    ],
    outcome: "Enterprise AI infrastructure without the DevOps overhead.",
  },
  {
    icon: MessageSquare,
    title: "Unified Communications Hub",
    bullets: [
      "One AI brain across WhatsApp, Telegram, Discord, Slack, Signal, and iMessage",
      "Persistent context and memory across every channel",
      "Granular role-based access per channel, team, or business unit",
    ],
    outcome: "Consistent AI experience across every platform your team already uses.",
  },
  {
    icon: Plug,
    title: "Enterprise Workflow Integration",
    bullets: [
      "Connect calendars, email, documents, CRMs, and internal tools",
      "Automate file management, reporting, and web-based tasks",
      "Intelligent model routing: Claude, GPT, Gemini, or your preferred LLM",
    ],
    outcome: "Your AI doesn't just answer questions — it executes across your stack.",
  },
  {
    icon: Bot,
    title: "Custom AI Agents",
    bullets: [
      "Purpose-built agents for operations, executive support, research, and compliance",
      "Automated workflows: scheduled reports, triggered actions, pipeline orchestration",
      "Enterprise guardrails and permission scoping for safe, auditable operation",
    ],
    outcome: "AI agents tailored to your business processes and governance requirements.",
  },
  {
    icon: LifeBuoy,
    title: "Legacy Migration & Recovery",
    bullets: [
      "Clean migration from legacy or broken AI installations",
      "Conflict resolution, service cleanup, and configuration repair",
      "Post-migration hardening with documentation and regression prevention",
    ],
    outcome: "From broken setup to production-grade — with full audit trail.",
  },
  {
    icon: Activity,
    title: "Managed Operations & SLA",
    bullets: [
      "Proactive health monitoring, alerting, and incident response",
      "Cost optimisation and model performance benchmarking",
      "Managed updates aligned with upstream releases and your change windows",
    ],
    outcome: "Dedicated operational ownership so your team can focus on outcomes.",
  },
  {
    icon: GraduationCap,
    title: "Executive Briefings & Team Enablement",
    bullets: [
      "Hands-on workshops: 'AI in daily operations' for leadership and staff",
      "Collaborative sessions to design workflows and build internal capability",
      "Strategic guidance on model selection, data privacy, and scaling",
    ],
    outcome: "Your organisation builds lasting AI competency — not just a vendor dependency.",
  },
];

export const ServiceModelsSection = () => {
  return (
    <section id="services" className="section-spacing overflow-hidden relative">

      <div className="section-container relative z-10">
        <div className="max-w-6xl mx-auto space-y-12">
          <motion.div
            className="text-center space-y-4"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.7 }}
          >
            <h2 className="text-3xl md:text-5xl font-display font-medium leading-tight">
              What we do
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              No pricing on this page. Just service models, capabilities, and outcomes.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-5">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.5, delay: index * 0.06 }}
                  className="relative group"
                >
                  {/* Gradient border glow on hover */}
                  <div className="absolute -inset-[1px] rounded-2xl bg-gradient-to-br from-[hsl(var(--coral-bright)/0.15)] via-transparent to-[hsl(var(--cyan-bright)/0.15)] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                  <div className="relative glass-card rounded-2xl p-6 space-y-4 h-full hover:bg-white/[0.03] transition-all duration-300">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[hsl(var(--coral-bright)/0.12)] to-[hsl(var(--cyan-bright)/0.08)] flex items-center justify-center">
                        <Icon className="w-5 h-5 text-primary" />
                      </div>
                      <h3 className="text-lg font-display font-medium">{service.title}</h3>
                    </div>
                    <ul className="space-y-2 text-sm text-muted-foreground">
                      {service.bullets.map((b, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <span className="h-1 w-1 rounded-full bg-[hsl(var(--cyan-bright))] mt-2 shrink-0" />
                          {b}
                        </li>
                      ))}
                    </ul>
                    <p className="text-sm font-medium text-accent pt-3">
                      → {service.outcome}
                    </p>
                    <Button variant="ghost" size="sm" className="text-primary/80 hover:text-primary hover:bg-white/5 px-0" asChild>
                      <a href="#contact">Talk about this →</a>
                    </Button>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};
